<template>
    <div class="card border-0 bg-white profile-settings-card rounded-0">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
            <form v-on:submit="submit">
                <div class="row">
                    <!-- LANG -->
                    <div class="col-md-6" v-if="update">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25" v-if="locales.length > 1">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.contents.lang') }}
                            </label>
                            <div class="d-flex gap-3 align-items-center">
                                <country-flag :country="lang" size="big" />
                                <select class="form-select shadow-none text-black p-15 pe-50 fs-14 fw-medium w-auto"
                                    v-model="lang" @change="changeLang()" required>
                                    <option v-for="locale in locales" :value="locale" :key="locale">{{
                                        $t(`feature.contents.$lang.${locale}`) }}</option>
                                </select>
                            </div>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.lang ?? false">
                                <p v-for="error in errors.lang" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- NAME -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.title.label') }}*
                            </label>
                            <input v-model="content.title![lang]" class="form-control shadow-none text-black" required
                                :class="[{ 'is-invalid': submitted && errors?.title }]" />
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.title ?? false">
                                <p v-for="error in errors.title" :key="error" class="small text-danger lh-1 m-0 mt-1">{{
                                    error }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="col-12"></div>
                    <!-- DESCRIPTION -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('form.description.label') }}
                            </label>
                            <textarea v-model="content.description![lang]" class="form-control shadow-none text-black"
                                :class="[{ 'is-invalid': submitted && errors?.description }]" rows="6"></textarea>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.description ?? false">
                                <p v-for="error in errors.description" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">
                                    {{
                                        error }}</p>
                            </div>
                        </div>
                    </div>
                    <!-- ACTIVE -->
                    <div class="col-12">
                        <div class="form-check form-group form-switch mb-15 mb-sm-20 mb-md-25">
                            <input class="form-check-input shadow-none me-8" type="checkbox" role="switch"
                                v-model="content.active" id="active" :true-value="1" :false-value="0">
                            <label class="form-check-label fw-medium position-relative top-1" for="active">
                                {{ $t('form.active.label') }}
                            </label>
                        </div>
                    </div>
                    <!-- COVER CONTENT -->
                    <div class="col-md-6">
                        <div class="form-group mb-15 mb-sm-20 mb-md-25">
                            <label class="d-block text-black fw-semibold mb-10">
                                {{ $t('feature.contents.$categories.imageCover') }}
                            </label>
                            <div>
                                <input class="form-control fs-15 text-black shadow-none" type="file" id="cover-picker"
                                    @change="event => fileUploaded(event)" accept="image/png, image/jpeg"
                                    ref="coverPickerRef">
                                <div class="col-12 mt-25" v-if="content.image_b64 && content.image_b64 !== ' '">
                                    <div class="position-relative">
                                        <div class="position-absolute end-0 z-1 m-10">
                                            <div data-bs-toggle="modal" :data-bs-target="`#deleteCover`"
                                                class="align-items-center bg-danger-subtle d-inline-flex fs-3 h-50 justify-content-center rounded-circle text-danger w-50 cursor-pointer">
                                                <i class="ph-bold ph-x"></i>
                                            </div>
                                        </div>
                                        <img :src="content.image_b64" alt="image">
                                    </div>
                                </div>
                            </div>
                            <!-- ERRORS -->
                            <div class="form-messages mt-2 px-1" v-if="errors?.image_b64 ?? false">
                                <p v-for="error in errors.image_b64" :key="error"
                                    class="small text-danger lh-1 m-0 mt-1">{{ error }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="col-12" v-if="loading">
                        <SpinnerLoading class="mb-25" />
                    </div>
                    <div class="col-md-12" v-else>
                        <div class="d-flex align-items-center justify-content-between gap-3 mb-25">
                            <!-- DELETE -->
                            <button v-if="update" type="button"
                                class="bg-transparent p-0 border-0 text-danger lh-1 fw-medium" data-bs-toggle="modal"
                                data-bs-target="#confirmModal">
                                <i class="ph ph-trash lh-1 me-1 position-relative top-2"></i>
                                <span class="position-relative">{{ $t('cta.delete_content') }}</span>
                            </button>
                            <span v-else></span>
                            <!-- SAVE -->
                            <div class="d-flex flex-wrap gap-3 justify-content-end ms-auto">
                                <DiscardButton @confirm="$router.push({ path: `/content-category/${category_id}` })" />
                                <button
                                    class="default-btn transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                                    type="submit" v-on:click="submitted = true">
                                    {{ $t('cta.save_content') }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- BLOCKS -->
                    <div class="accordion border-0" id="block-form-accordion" v-if="content.content_blocks">
                        <DraggableList :list="content.content_blocks" item-key="id" handle=".block-form .block-handle"
                            sort="true" @start="dragStart" @change="orderBlocks">
                            <template #item="block">
                                <div class="accordion-item block-form border-0 rounded-0 border-0">
                                    <button
                                        class="accordion-button d-block w-100 text-start text-black fs-14 fs-md-15 fs-lg-16 fw-medium rounded-0 bg-white border-0 collapsed p-15 p-sm-20 p-sm-25 p-lg-30 pe-50"
                                        type="button" data-bs-toggle="collapse"
                                        :data-bs-target="`#block-tab-${block.element.id}`" aria-expanded="false"
                                        :aria-controls="`block-tab-${block.element.id}`">
                                        <div class="d-sm-flex align-items-center justify-content-between pe-3">
                                            <div class="d-flex gap-3 align-items-center">
                                                <div class="block-handle fw-bold text-muted">
                                                    {{ block.index + 1 }}
                                                </div>
                                                <h5 class="fw-medium text-black-emphasis mb-0 flex-grow-1">{{
                                                    block.element.subtitle[lang] }}
                                                </h5>
                                            </div>
                                            <span class="text-muted text-uppercase small badge bg-light">
                                                {{ $t(`feature.contents.$blocks.$type.${block.element.type}`) }}
                                            </span>
                                        </div>
                                    </button>
                                    <div :id="`block-tab-${block.element.id}`"
                                        class="accordion-collapse collapse border-0"
                                        data-bs-parent="#block-form-accordion">
                                        <div class="border-0 mt-20 py-15 py-sm-20 py-sm-25 py-lg-30">
                                            <!-- BODY -->
                                            <ContentBlockForm :block="block.element" :key="block.element.id"
                                                :locale="lang" @update="updated => updateBlock(block.index, updated)"
                                                @changeLang="(event) => changeLang(event)"
                                                @delete="deleteBlock(block.index)" />
                                        </div>
                                    </div>
                                </div>

                            </template>
                        </DraggableList>
                    </div>
                    <!--  -->

                    <!-- ADD -->
                    <div class="d-flex justify-content-between gap-3 mt-25 flex-wrap" v-if="update">
                        <p class="mb-8">{{ $t('feature.contents.$blocks.drag_reorder') }}</p>
                        <button
                            class="ms-auto default-btn outlined transition fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 fs-md-15 fs-lg-16"
                            type="button" @click="addBlock">
                            {{ $t('feature.contents.$blocks.add') }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
    <!-- ERROR MESSAGE -->
    <div class="row justify-content-center">
        <div class="col">
            <div class="alert alert-danger alert-dismissible fade border-0 border-danger-subtle border-top rounded-0 m-0 mb-25"
                role="alert" id="errorMessage">
                <strong>{{ $t('error.something_went_wrong') }}</strong>
                <button type="button" class="btn-close" v-on:click="hideError" aria-label="Close"></button>
            </div>
        </div>
    </div>
    <ConfirmModal @confirm="deleteContent()" :title="$t('cta.$delete.title')" :message="$t('cta.$delete.message')" />
    <ContentBlockModal id="block_modal" :content_id="content.id!" :order="content.content_blocks?.length" @save="event => saveBlock(event)" v-if="update" />
    <ConfirmModal @confirm="deleteCover()" :id="`deleteCover`" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" />
</template>

<style lang="scss">
.accordion .accordion-item .accordion-button {
    --splash-danger-color: transparent;
    padding-top: 15px !important;
    padding-bottom: 15px !important;
    border-bottom: none !important;
    display: block;
}

.block-handle {
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: #e9ecef;
    box-shadow: 0px 4px 34px rgba(101, 96, 240, 0.1) !important;
}

.sortable-chosen {
    .accordion-button {
        outline: 1px solid !important;
        outline-color: #d1d5db !important;
    }

    .block-handle {
        pointer-events: all !important;
        cursor: grabbing !important;
        cursor: -moz-grabbing !important;
        cursor: -webkit-grabbing !important;
    }

    &.sortable-ghost {
        .block-handle {
            pointer-events: all !important;
            cursor: grabbing !important;
            cursor: -moz-grabbing !important;
            cursor: -webkit-grabbing !important;
        }

        .card-body {
            outline: none;
            box-shadow: none;
        }
    }
}
</style>

<script lang="ts">
import { defineComponent, ref } from "vue";
import ConfirmModal from "@/components/Widgets/ConfirmModal.vue";
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import { removeNullProperties } from "@/utils/methods/remove_null_properties";
import API from "@/services/api/api.service";
import { OrderField } from "@/utils/models/_model";
import { ContentBlock, ContentInfo } from "@/utils/models/content.model";
import ContentBlockModal from "@/components/FK/Contents/Blocks/ContentBlockModal.vue";
import ContentBlockForm from "@/components/FK/Contents/Blocks/ContentBlockForm.vue";
import { Modal } from "bootstrap";
import getBase64 from "@/utils/methods/get_base_64";
import DiscardButton from "@/components/Widgets/DiscardButton.vue";
import CountryFlag from 'vue-country-flag-next';
import config from "@/utils/const/config";
import { i18n } from '@/assets/i18n/i18n';

export default defineComponent({
    setup() {
        const coverPickerRef = ref();
        return {
            coverPickerRef,
        };
    },


    name: "ContentForm",
    components: {
        ConfirmModal,
        SpinnerLoading,
        ContentBlockModal,
        ContentBlockForm,
        DiscardButton,
        CountryFlag
    },
    data: function () {
        const _content = this.$route.meta.content as ContentInfo;
        return {
            content: ({
                ..._content as ContentInfo,
                content_category_id: _content?.content_category_id ?? this.$route.params.id,
                title: _content?.title ?? {},
                description: _content?.description ?? {}
            }) as ContentInfo,
            submitted: false,
            loading: false,
            errors: undefined as any,
            blockModal: null as Modal | null,
            originalBlocks: undefined as ContentBlock[] | undefined,
            lang: (this.$route.query.lang ?? i18n.global.locale) as string,
        }
    },
    mounted() {
        if (this.update) {
            this.blockModal = new Modal('#block_modal', { backdrop: 'static' })
        }
    },
    computed: {
        update(): boolean {
            return this.$route.meta.content !== undefined && this.$route.meta.content !== null;
        },
        category_id(): string {
            return this.content?.content_category_id!
        },
        locales(): string[] {
            return config.available_locales
        }
    },
    methods: {
        submit(event): void {
            this.errors = undefined;
            event.preventDefault();
            console.log("CONTENT", this.content);
            removeNullProperties<ContentInfo>(this.content);

            this.loading = true;
            if (this.update) {
                console.log('UPDATE');
                Promise.all([
                    API.updateContent({
                        ...this.content,
                        title: this.content.title?.[i18n.global.locale],
                        description: undefined,
                    }),
                    API.updateContentLocalization(this.content, this.lang)
                ]).then((response) => {
                    // this.$router.push(`/content-category/${this.content?.content_category_id!}`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            } else {
                console.log('CREATE');

                this.content.content_category_id = this.category_id;

                API.createContentLocalization(this.content).then((response) => {
                    this.$router.push(`/content/${response.data.data.id}/edit`);
                }).catch((error) => {
                    this.errors = error.response?.data?.errors;
                    this.showError();
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
        deleteContent(): void {
            this.loading = true;
            API.deleteContent(this.content.id!).then((response) => {
                if (response.data.success === true) {
                    this.$router.push(`/content-category/${this.content?.content_category_id!}`);
                }
            }).catch((error) => {
                this.showError();
            }).finally(() => {
                console.log("DELETED_CONTENT_ID", this.content.id!);
                this.loading = false;
            })
        },
        showError() {
            const error = document.getElementById('errorMessage');
            error?.classList.add('show');
            setTimeout(() => {
                this.hideError();
            }, 5000)
        },
        hideError() {
            const error = document.getElementById('errorMessage');
            error?.classList.remove('show');
        },
        addBlock() {
            this.blockModal?.show();
        },
        saveBlock(block: ContentBlock) {
            this.content.content_blocks?.push(block);
            this.blockModal?.hide();
        },
        updateBlock(index: number, block: ContentBlock) {
            this.content.content_blocks![index] = block;
            console.log(this.content);
        },
        deleteBlock(index: number) {
            this.content.content_blocks?.splice(index, 1);
        },
        dragStart() {
            this.originalBlocks = [...this.content.content_blocks ?? []];
        },
        orderBlocks(event, originalEvent) {
            console.log('event', event);
            console.log('original_event', originalEvent);
            console.log('blocks', this.content.content_blocks);

            let order: OrderField[] | undefined = this.content.content_blocks?.map((block, index) => ({ id: block.id!, order: index } as OrderField));
            if (order !== undefined) {
                API.orderContentBlocks({ content_id: this.content.id!, blocks_order: order }).then((response) => {
                    if (response.data.success === true) {
                        this.originalBlocks = [...this.content.content_blocks ?? []];
                    } else {
                        throw Error('ORDER_BLOCK_ERROR');
                    }
                }).catch((error) => {
                    this.content.content_blocks = [...this.originalBlocks ?? []];
                    console.error(error);
                })
            }
        },
        async fileUploaded($event) {
            if ($event.target.value) {
                this.content.image_b64 = await getBase64($event.target.files[0]);
            }
        },
        deleteCover() {
            this.content.image_b64 = " ";
            if (this.coverPickerRef) {
                this.coverPickerRef.value = null;
            }

        },
        changeLang(lang?: string) {
            console.log(lang);
            if (lang !== undefined) {
                this.lang = lang;
            }
        }
    },
});
</script>